import { stride } from 'stridejs'
import { useAtomValue } from 'jotai'
import { useMutation } from '@tanstack/react-query'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'

import { CHAIN_INFO_LIST } from '@/config'
import { assert } from '@/utils'
import { convertDenomToMicroDenom, calculateFeeWithMinimalDenom } from '@/wallet-utils'
import { stakeAmountAtom } from '@/page-components/Stake/atoms'
import { useReferral } from '@/page-components/Stake/Referral'
import { getLiquidStakeSafetyDeduction2 } from '../utils'
import { MutationParameters } from './types'
import { useSelectedDenom } from '../../../../contexts/denom'
import { useStrideChain, useStrideChainBalance } from '@/contexts/wallet'

const useSignLiquidStakeMutation = ({ setLiquidStakeRaw }: MutationParameters) => {
  const { memo } = useReferral()

  const [denom] = useSelectedDenom()

  const { address: strideAddress, getSigningStargateClient } = useStrideChain()

  const { data: strideBalances } = useStrideChainBalance()

  const amount = useAtomValue(stakeAmountAtom)

  const handleMutation = async (): Promise<TxRaw> => {
    assert(strideAddress, 'You are unable to sign liquid staking without connecting your stride wallet.')

    const chainInfo = CHAIN_INFO_LIST[denom]

    const amountInMicroDenom = convertDenomToMicroDenom(amount, chainInfo.stakeCurrency.coinMinimalDenom)

    const deductible = getLiquidStakeSafetyDeduction2({
      amount: amountInMicroDenom,
      denom,
      strideBalances: strideBalances?.balances ?? []
    })

    const amountWithSafetyDeduction = amountInMicroDenom - deductible

    const getMsgLiquidStake = () => {
      if (denom === 'DYM') {
        return stride.stakedym.MessageComposer.withTypeUrl.liquidStake({
          staker: strideAddress,
          nativeAmount: amountWithSafetyDeduction.toString()
        })
      }

      return stride.stakeibc.MessageComposer.withTypeUrl.liquidStake({
        creator: strideAddress,
        amount: amountWithSafetyDeduction.toString(),
        hostDenom: chainInfo.stakeCurrency.coinMinimalDenom
      })
    }

    const msgLiquidStake = getMsgLiquidStake()

    const client = await getSigningStargateClient()

    const gas = await client.simulate(strideAddress, [msgLiquidStake], '')

    // @Q: Can we use this and deduct its value to `getMsgLiquidStake`?
    // @TODO: This probably should be selectedAccount - we should prioritize using the host token as gas instead of STRD
    const fee = calculateFeeWithMinimalDenom(CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom, gas)

    return await client.sign(strideAddress, [msgLiquidStake], fee, memo)
  }

  const handleSuccess = (raw: TxRaw) => {
    setLiquidStakeRaw(raw)
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useSignLiquidStakeMutation }
